import { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Trash2, TrendingUp } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { useStore } from '../store/useStore';
import { formatCurrency } from '../lib/utils';

export interface SalesEntry {
  id: string;
  month: string; // ISO date (first of month)
  companyName: string;
  total: number;
  designAmount: number | null;
  devAmount: number | null;
  designHours: number | null;
  devHours: number | null;
  note: string;
  clientKey: string;
  isNewClient: boolean;
  salesPersonId: string | null;
  designer: string;
  designDoneHours: number | null;
  designEstimate: string;
  designNote: string;
  designStartDate: string | null;
  designEndDate: string | null;
  designHidden: boolean;
}

/** Standard hourly price — effective €/h is coloured against this. */
export const TARGET_RATE = 70;

/**
 * Effective hours for a €-column: the manually entered value wins; otherwise
 * the hours budget is derived as amount / 70. Clearing the manual value
 * returns the cell to the automatic calculation.
 */
export function effHours(amount: number | null, manual: number | null): number | null {
  if (manual != null) return manual;
  if (amount != null && amount > 0) return amount / TARGET_RATE;
  return null;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

const ET_MONTHS = ['JAANUAR', 'VEEBRUAR', 'MÄRTS', 'APRILL', 'MAI', 'JUUNI', 'JUULI', 'AUGUST', 'SEPTEMBER', 'OKTOOBER', 'NOVEMBER', 'DETSEMBER'];

function monthKey(iso: string): string {
  return iso.split('T')[0].slice(0, 7); // yyyy-MM
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return `${ET_MONTHS[m - 1]} ${String(y).slice(2)}`;
}

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function rateColor(rate: number | null): string {
  if (rate == null || !isFinite(rate)) return 'text-gray-300';
  if (rate >= TARGET_RATE) return 'text-green-600';
  if (rate >= TARGET_RATE * 0.8) return 'text-amber-600';
  return 'text-red-500';
}

function fmtRate(amount: number | null, hrs: number | null): string | null {
  if (amount == null || !hrs) return null;
  return `${Math.round(amount / hrs)} €/h`;
}

// ── Inline-editable cells ────────────────────────────────────────────────────

function MoneyCell({
  value,
  onSave,
  bold = false,
}: {
  value: number | null;
  onSave: (v: number | null) => void;
  bold?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState('');

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const v = local.trim() === '' ? null : Number(local);
          if (v !== value) onSave(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="w-24 px-1.5 py-0.5 text-sm text-right border border-brand-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-400"
      />
    );
  }
  return (
    <button
      onClick={() => { setLocal(value != null ? String(value) : ''); setEditing(true); }}
      className={`w-full px-1.5 py-0.5 text-sm text-right rounded hover:bg-brand-50 ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'} ${value == null ? 'text-gray-300' : ''}`}
      title="Muuda summat"
    >
      {value != null ? formatCurrency(value) : '—'}
    </button>
  );
}

function HoursCell({
  value,
  autoValue,
  onSave,
}: {
  value: number | null;
  autoValue: number | null; // derived amount / 70 shown when no manual value
  onSave: (v: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState('');
  const shown = value ?? autoValue;

  if (editing) {
    return (
      <input
        autoFocus
        type="number"
        min={0}
        step={0.5}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const v = local.trim() === '' ? null : Number(local);
          if (v !== value) onSave(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="w-16 px-1 py-0.5 text-xs text-right border border-brand-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-400"
      />
    );
  }
  return (
    <button
      onClick={() => { setLocal(shown != null ? String(round1(shown)) : ''); setEditing(true); }}
      className={`px-1 py-0.5 text-xs rounded hover:bg-brand-50 whitespace-nowrap ${value != null ? 'text-gray-600 font-medium' : 'text-gray-400'}`}
      title={value != null ? 'Käsitsi sisestatud tunnid — tühjenda, et naasta automaatse (summa / 70) juurde' : 'Automaatne tundide eelarve: summa / 70. Vajuta, et sisestada tegelik'}
    >
      {shown != null ? `${value == null ? '≈ ' : ''}${round1(shown)} h` : '+ h'}
    </button>
  );
}

function TextCell({
  value,
  onSave,
  placeholder,
  className = '',
}: {
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);

  if (editing) {
    return (
      <input
        autoFocus
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          setEditing(false);
          if (local.trim() !== value) onSave(local.trim());
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setEditing(false);
        }}
        className={`w-full px-1.5 py-0.5 text-sm border border-brand-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-400 ${className}`}
      />
    );
  }
  return (
    <button
      onClick={() => { setLocal(value); setEditing(true); }}
      className={`w-full px-1.5 py-0.5 text-sm text-left rounded hover:bg-brand-50 ${value ? 'text-gray-800' : 'text-gray-300 italic'} ${className}`}
      title="Muuda"
    >
      {value || placeholder || '—'}
    </button>
  );
}

// ── Money + hours stacked column cell ────────────────────────────────────────

function AmountHoursCell({
  amount,
  hoursVal,
  onAmount,
  onHours,
}: {
  amount: number | null;
  hoursVal: number | null;
  onAmount: (v: number | null) => void;
  onHours: (v: number | null) => void;
}) {
  const h = effHours(amount, hoursVal);
  const rate = amount != null && h ? amount / h : null;
  const isAuto = hoursVal == null;
  return (
    <div className="flex flex-col items-end">
      <MoneyCell value={amount} onSave={onAmount} />
      <div className="flex items-center gap-1">
        <HoursCell value={hoursVal} autoValue={amount != null && amount > 0 ? amount / TARGET_RATE : null} onSave={onHours} />
        {rate != null && (
          <span className={`text-[10px] font-medium ${isAuto ? 'text-gray-300' : rateColor(rate)}`}>{Math.round(rate)} €/h</span>
        )}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Sales() {
  const currentUser = useStore((s) => s.currentUser);
  const users = useStore((s) => s.users);
  const projects = useStore((s) => s.projects);
  const [entries, setEntries] = useState<SalesEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMonth, setNewMonth] = useState(currentMonthKey());

  const load = useCallback(async () => {
    try {
      setError('');
      const r = await apiClient.get<{ entries: SalesEntry[] }>('/sales');
      setEntries(r.entries);
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canUse = currentUser && ['super_admin', 'admin', 'sales', 'sales_pm'].includes(currentUser.role);

  // Options for the salesperson selector: people who actually sell — i.e.
  // anyone set as a project's sales owner or already assigned on a ledger
  // row. The seed "Admin" service account is not a person and is excluded.
  const salespeople = useMemo(() => {
    const ids = new Set(projects.map((pr) => pr.salesOwnerId));
    for (const e of entries) if (e.salesPersonId) ids.add(e.salesPersonId);
    return users
      .filter((u) => ids.has(u.id) && !(u.name === 'Admin' || u.email === 'admin@vdisain.ee'))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, entries, users]);

  const groups = useMemo(() => {
    const m = new Map<string, SalesEntry[]>();
    for (const e of entries) {
      const key = monthKey(e.month);
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(e);
    }
    return Array.from(m.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries]);

  const yearStats = useMemo(() => {
    const year = String(new Date().getFullYear());
    const rows = entries.filter((e) => e.month.startsWith(year));
    const total = rows.reduce((s, e) => s + Number(e.total), 0);
    const hoursSum = rows.reduce(
      (s, e) =>
        s +
        (effHours(e.designAmount != null ? Number(e.designAmount) : null, e.designHours != null ? Number(e.designHours) : null) ?? 0) +
        (effHours(e.devAmount != null ? Number(e.devAmount) : null, e.devHours != null ? Number(e.devHours) : null) ?? 0),
      0,
    );
    return { total, hours: hoursSum, rate: hoursSum > 0 ? total / hoursSum : null };
  }, [entries]);

  if (!canUse) {
    return <div className="p-8 text-center text-gray-400">This page is available to admins and salespeople only.</div>;
  }

  const patch = async (id: string, updates: Record<string, unknown>) => {
    try {
      await apiClient.patch(`/sales/${id}`, updates);
      await load();
    } catch (e) {
      useStore.getState().addError('updateSalesEntry', e);
    }
  };

  const addRow = async (month: string) => {
    try {
      await apiClient.post('/sales', { month, companyName: 'Uus klient', total: 0, note: '' });
      await load();
    } catch (e) {
      useStore.getState().addError('addSalesEntry', e);
    }
  };

  const removeRow = async (id: string) => {
    try {
      await apiClient.delete(`/sales/${id}`);
      await load();
    } catch (e) {
      useStore.getState().addError('deleteSalesEntry', e);
    }
  };

  const grid = 'grid grid-cols-[1fr_130px_130px_130px_36px] gap-2';

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Müük</h1>
          <p className="text-gray-500 text-sm mt-1">
            Uued kliendid kuude kaupa · {new Date().getFullYear()} kokku{' '}
            <span className="font-semibold text-gray-800">{formatCurrency(yearStats.total)}</span>
            {yearStats.rate != null && (
              <>
                {' '}· {Math.round(yearStats.hours)} h ·{' '}
                <span className={`font-semibold ${rateColor(yearStats.rate)}`}>{Math.round(yearStats.rate)} €/h</span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={newMonth}
            onChange={(e) => setNewMonth(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-700 bg-white"
          />
          <button
            onClick={() => newMonth && addRow(newMonth)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg text-sm font-medium hover:bg-brand-600"
          >
            <Plus size={15} /> Lisa rida
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 mb-4">{error}</div>
      )}

      {loading && <div className="p-12 text-center text-gray-400 text-sm">Laen…</div>}

      {!loading && groups.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <TrendingUp size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400 text-sm">Kandeid pole veel. Lisa esimene rida ülevalt.</p>
        </div>
      )}

      <div className="space-y-6">
        {groups.map(([key, rows]) => {
          const sum = (f: (r: SalesEntry) => number) => rows.reduce((s, r) => s + f(r), 0);
          const totals = {
            total: sum((r) => Number(r.total)),
            design: sum((r) => Number(r.designAmount ?? 0)),
            dev: sum((r) => Number(r.devAmount ?? 0)),
            designH: sum((r) => effHours(r.designAmount != null ? Number(r.designAmount) : null, r.designHours != null ? Number(r.designHours) : null) ?? 0),
            devH: sum((r) => effHours(r.devAmount != null ? Number(r.devAmount) : null, r.devHours != null ? Number(r.devHours) : null) ?? 0),
          };
          const allH = totals.designH + totals.devH;
          return (
            <div key={key} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {/* Month header with totals */}
              <div className={`px-5 py-3 bg-gray-50 border-b border-gray-100 ${grid} items-center`}>
                <span className="text-sm font-bold text-gray-900">{monthLabel(key)}</span>
                <div className="flex flex-col items-end">
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(totals.total)}</span>
                  {allH > 0 && (
                    <span className={`text-[10px] font-medium ${rateColor(totals.total / allH)}`}>
                      {Math.round(allH)} h · {Math.round(totals.total / allH)} €/h
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-sm font-semibold text-purple-600">{formatCurrency(totals.design)}</span>
                  {totals.designH > 0 && (
                    <span className={`text-[10px] font-medium ${rateColor(totals.design / totals.designH)}`}>
                      {Math.round(totals.designH)} h · {Math.round(totals.design / totals.designH)} €/h
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-sm font-semibold text-blue-600">{formatCurrency(totals.dev)}</span>
                  {totals.devH > 0 && (
                    <span className={`text-[10px] font-medium ${rateColor(totals.dev / totals.devH)}`}>
                      {Math.round(totals.devH)} h · {Math.round(totals.dev / totals.devH)} €/h
                    </span>
                  )}
                </div>
                <span />
              </div>
              {/* Column labels */}
              <div className={`px-5 py-1.5 border-b border-gray-50 ${grid}`}>
                <span className="text-xs text-gray-400">Ettevõte / märkus</span>
                <span className="text-xs text-gray-400 text-right">Kokku · h · €/h</span>
                <span className="text-xs text-gray-400 text-right">Disain · h · €/h</span>
                <span className="text-xs text-gray-400 text-right">Arendus · h · €/h</span>
                <span />
              </div>
              <div className="divide-y divide-gray-50">
                {rows.map((row) => {
                  const rowHours =
                    (effHours(row.designAmount != null ? Number(row.designAmount) : null, row.designHours != null ? Number(row.designHours) : null) ?? 0) +
                    (effHours(row.devAmount != null ? Number(row.devAmount) : null, row.devHours != null ? Number(row.devHours) : null) ?? 0);
                  const blended = rowHours > 0 ? Number(row.total) / rowHours : null;
                  const rowAuto = row.designHours == null && row.devHours == null;
                  return (
                    <div key={row.id} className={`px-5 py-1.5 ${grid} items-center group`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <TextCell
                            value={row.companyName}
                            onSave={(v) => v && patch(row.id, { companyName: v })}
                            placeholder="Ettevõtte nimi"
                            className="font-medium"
                          />
                          {row.isNewClient && (
                            <span className="shrink-0 text-[9px] font-bold text-green-700 bg-green-100 px-1 py-0.5 rounded" title="Uus klient (esimene tehing)">
                              UUS
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="flex-1 min-w-0">
                            <TextCell
                              value={row.note}
                              onSave={(v) => patch(row.id, { note: v })}
                              placeholder="märkus…"
                              className="text-xs"
                            />
                          </div>
                          <select
                            value={row.salesPersonId ?? ''}
                            onChange={(e) => patch(row.id, { salesPersonId: e.target.value || null })}
                            className={`shrink-0 text-[10px] border-0 bg-transparent rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-brand-400 ${row.salesPersonId ? 'text-gray-500' : 'text-gray-300'}`}
                            title="Müügijuht"
                          >
                            <option value="">— müügijuht</option>
                            {salespeople.map((u) => (
                              <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <MoneyCell value={Number(row.total)} bold onSave={(v) => patch(row.id, { total: v ?? 0 })} />
                        {blended != null && (
                          <span className={`text-[10px] font-medium ${rowAuto ? 'text-gray-300' : rateColor(blended)}`}>
                            {rowAuto ? '≈ ' : ''}{Math.round(rowHours)} h · {Math.round(blended)} €/h
                          </span>
                        )}
                      </div>
                      <AmountHoursCell
                        amount={row.designAmount != null ? Number(row.designAmount) : null}
                        hoursVal={row.designHours != null ? Number(row.designHours) : null}
                        onAmount={(v) => patch(row.id, { designAmount: v })}
                        onHours={(v) => patch(row.id, { designHours: v })}
                      />
                      <AmountHoursCell
                        amount={row.devAmount != null ? Number(row.devAmount) : null}
                        hoursVal={row.devHours != null ? Number(row.devHours) : null}
                        onAmount={(v) => patch(row.id, { devAmount: v })}
                        onHours={(v) => patch(row.id, { devHours: v })}
                      />
                      <button
                        onClick={() => removeRow(row.id)}
                        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity justify-self-end"
                        title="Kustuta rida"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 py-2 border-t border-gray-50">
                <button
                  onClick={() => addRow(key)}
                  className="flex items-center gap-1.5 text-xs text-brand-500 hover:text-brand-700 font-medium"
                >
                  <Plus size={12} /> Lisa rida siia kuusse
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
